import { faSpinner } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { exhaustive } from "exhaustive"
import { sortBy } from "lodash"
import { FC, useEffect, useState } from "react"
import { useClient } from "../../Client/ClientAndUserProvider"
import { ConsumerCatalogConfiguration } from "../../Client/consumerCatalogConfigurationTypes"
import { Loader } from "../../Loader/Loader"
import { API } from "../../network/API"
import { FinalizeButton } from "../../Orders/Components/Form/Buttons/Buttons"
import { ModulChooserButton } from "../../Orders/Components/ModulChooserButton/ModulChooserButton"
import { ModulePopup } from "../../Orders/Components/ModulePopup/ModulePopup"
import { PermissionAreaLocation, usePermissions } from "../../PermissionContext"
import {
	ConsumerGroup,
	DynamicGroup,
	GetDynamicGroupsResponse,
	vatPolicyToHumanBooleanText,
} from "../CustomerPortalConsumerGroups/CustomerPortalConsumerGroups"
import style from "./CustomerPortalConsumerGroupSelectionModal.module.css"

type CustomerPortalConsumerGroupSelectionProps = {
	consumerRef: string
	currentDynamicGroup: DynamicGroup | null
	fixedGroupFallback: ConsumerGroup
	onClose: () => void
	onDone: () => void
}

type SetConsumerGroupConnectionRequest = {
	consumerRef: string
	group: SetConsumerGroupConnection
}

type SetConsumerGroupConnection = SetConsumerGroupConnectionFixed | SetConsumerGroupConnectionDynamic

type SetConsumerGroupConnectionFixed = {
	type: "Fixed"
}

type SetConsumerGroupConnectionDynamic = {
	type: "Dynamic"
	dynamicGroupId: string
}

enum SubmitFailType {
	Generic = "Generic",
}

export const CustomerPortalConsumerGroupSelectionModal: FC<CustomerPortalConsumerGroupSelectionProps> = (
	props: CustomerPortalConsumerGroupSelectionProps,
) => {
	const client = useClient()
	const permissions = usePermissions()

	const [dynamicGroups, setDynamicGroups] = useState<DynamicGroup[]>([])

	const [submitting, setSubmitting] = useState(false)
	const [submitFailed, setSubmitFailed] = useState<SubmitFailType | null>(null)
	const [selectedGroup, setSelectedGroup] = useState<"fixedFallback" | DynamicGroup>(
		props.currentDynamicGroup || "fixedFallback",
	)

	const [dataLoading, setDataLoading] = useState(true)

	useEffect(() => {
		fetchAvailableDynamicGroups()
	}, [])

	async function fetchAvailableDynamicGroups() {
		setDataLoading(true)
		setDynamicGroups([])

		if (
			!permissions.isAllowed(PermissionAreaLocation.Client_Consumer_Groups_Read) ||
			!client.features.allowSettingConsumerGroupConnections
		) {
			setDataLoading(false)
			return
		}

		await API.getWithRetries<GetDynamicGroupsResponse>(
			`/customer-portal/dynamic-consumer-groups-v1/${client.identifier}`,
			true,
		)
			.then((res) => {
				setDynamicGroups(
					sortBy(res.dynamicGroups, (dynamicGroup) => {
						return dynamicGroup.group.name.normalize("NFC").toLocaleLowerCase()
					}),
				)
			})
			.catch(() => {
				setDynamicGroups([])
			})

		setDataLoading(false)
	}

	function dynamicGroupsSection() {
		return (
			<div className={style.groupsWrapper}>
				{dynamicGroups.map((dynamicGroup) => {
					const isSelected =
						selectedGroup && selectedGroup !== "fixedFallback" ? selectedGroup.id === dynamicGroup.id : false
					return (
						<div
							key={dynamicGroup.id}
							className={style.consumerGroup}
							onClick={() => {
								setSelectedGroup(dynamicGroup)
							}}>
							<div className={style.consumerGroupContent}>
								<div className={style.name}>{dynamicGroup.group.name}</div>
								{dynamicGroupConfigDescription(dynamicGroup.group.consumerCatalogConfiguration)}
							</div>
							<ModulChooserButton
								className={style.consumerGroupSelectButton}
								selected={isSelected}
								onClick={(e) => {
									e.stopPropagation()
									setSelectedGroup(dynamicGroup)
								}}>
								{isSelected ? "Vald" : "Välj"}
							</ModulChooserButton>
						</div>
					)
				})}
				<div
					className={style.consumerGroup}
					onClick={() => {
						setSelectedGroup("fixedFallback")
					}}>
					<div className={style.consumerGroupContent}>
						<div className={style.name}>{props.fixedGroupFallback.name}</div>
						{dynamicGroupConfigDescription(props.fixedGroupFallback.consumerCatalogConfiguration)}
					</div>
					<ModulChooserButton
						className={style.consumerGroupSelectButton}
						selected={selectedGroup === "fixedFallback"}
						onClick={(e) => {
							e.stopPropagation()
							setSelectedGroup("fixedFallback")
						}}>
						{selectedGroup === "fixedFallback" ? "Vald" : "Välj"}
					</ModulChooserButton>
				</div>
			</div>
		)
	}

	function dynamicGroupConfigDescription(consumerCatalogConfiguration: ConsumerCatalogConfiguration) {
		return exhaustive(consumerCatalogConfiguration, "type", {
			Contract: () => {
				return (
					<>
						<div>Typ: Befintligt kontrakt</div>
						<div>
							Priser visas: <strong>Inga priser</strong>
						</div>
					</>
				)
			},
			Postpay: (it) => {
				return (
					<>
						<div>Typ: Efterskottsbetalning</div>
						<div>
							Priser visas: <strong>{vatPolicyToHumanBooleanText(it.preferredVATRenderPolicy)}</strong>
						</div>
					</>
				)
			},
			Prepay: (it) => {
				return (
					<>
						<div>Typ: Förskottsbetalning</div>
						<div>
							Priser visas: <strong>{vatPolicyToHumanBooleanText(it.preferredVATRenderPolicy)}</strong>
						</div>
					</>
				)
			},
		})
	}

	function onSubmit() {
		if (!selectedGroup) {
			return
		}

		let group: SetConsumerGroupConnection

		if (selectedGroup === "fixedFallback") {
			group = {
				type: "Fixed",
			}
		} else {
			group = {
				type: "Dynamic",
				dynamicGroupId: selectedGroup.id,
			}
		}

		const req: SetConsumerGroupConnectionRequest = {
			consumerRef: props.consumerRef,
			group: group,
		}

		API.postWithRetries<void, SetConsumerGroupConnectionRequest>(
			`/customer-portal/dynamic-consumer-groups-v1/${client.identifier}/connect-directly`,
			req,
			undefined,
			10,
		)
			.then(() => {
				props.onDone()
			})
			.catch(() => {
				setSubmitting(false)
				/*				if (err instanceof ServerError) {
					if (err?.data && isPlainObject(err?.data) && "message" in err?.data) {
						const message = err.data.message

						if (message === "group_not_found") {
							setSubmitFailed(SubmitFailType.GroupNotFound)
						} else {
							setSubmitFailed(SubmitFailType.Generic)
						}
						return
					}
				}*/
				setSubmitFailed(SubmitFailType.Generic)
			})
	}

	if (
		!permissions.isAllowed(PermissionAreaLocation.Client_Consumer_Groups_Read) ||
		!client.features.allowSettingConsumerGroupConnections
	) {
		return null
	}

	return (
		<ModulePopup
			disableClose={submitting}
			className={style.modal}
			onClose={() => {
				props.onClose()
			}}>
			<div className={style.wrapper}>
				<div className={style.modalHeader}>Koppla kund till grupp</div>
				{dataLoading ? (
					<div style={{ display: "flex", justifyContent: "center" }}>
						<Loader />
					</div>
				) : (
					dynamicGroupsSection()
				)}
				<div className={style.buttons}>
					<FinalizeButton
						disabled={submitting}
						onClick={() => {
							onSubmit()
						}}>
						Spara
						{submitting ? (
							<FontAwesomeIcon style={{ color: "gray", marginLeft: "15px" }} spin={true} icon={faSpinner} />
						) : null}
					</FinalizeButton>
				</div>
				{submitFailed ? (
					<div style={{ color: "var(--invalid-color)", margin: "15px 0 10px 0" }}>
						{exhaustive(submitFailed, {
							[SubmitFailType.Generic]: () => "Något gick fel, vänligen försök igen",
						})}
					</div>
				) : null}
			</div>
		</ModulePopup>
	)
}
